import React from 'react';

/**
 * Status indicator props interface
 */
interface StatusIndicatorProps {
  status: 'active' | 'inactive' | 'pending' | 'error' | 'success' | 'warning';
  text?: string;
  showPulse?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * Enhanced status indicator component with visual enhancements
 * Provides consistent status display across the application
 */
const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  status,
  text,
  showPulse = false,
  size = 'md',
  className = ''
}) => {
  // Determine badge classes based on status
  const getBadgeClasses = () => {
    let badgeClass = 'badge ';
    
    // Size classes
    if (size === 'sm') badgeClass += 'badge-sm ';
    else if (size === 'lg') badgeClass += 'badge-lg ';
    
    // Status color classes
    switch (status) {
      case 'active':
        badgeClass += 'bg-success';
        break;
      case 'inactive':
        badgeClass += 'bg-warning text-dark';
        break;
      case 'pending':
        badgeClass += 'bg-secondary';
        break;
      case 'error':
        badgeClass += 'bg-danger';
        break;
      case 'success':
        badgeClass += 'bg-success';
        break;
      case 'warning':
        badgeClass += 'bg-warning text-dark';
        break;
      default:
        badgeClass += 'bg-secondary';
    }
    
    // Add pulse animation for active states if requested
    if (showPulse && (status === 'active' || status === 'success')) {
      badgeClass += ' status-pulse';
    }
    
    return badgeClass + ' ' + className;
  };
  
  // Determine display text
  const getDisplayText = () => {
    if (text) return text;
    
    switch (status) {
      case 'active':
        return 'Active';
      case 'inactive':
        return 'Inactive';
      case 'pending':
        return 'Pending';
      case 'error':
        return 'Error';
      case 'success':
        return 'Success';
      case 'warning':
        return 'Warning';
      default:
        return 'Unknown';
    }
  };
  
  // Icon mapping for status types
  const getStatusIcon = () => {
    switch (status) {
      case 'active':
        return '●'; // Green dot for active
      case 'inactive':
        return '○'; // Hollow circle for inactive
      case 'pending':
        return '◐'; // Half-filled circle for pending
      case 'error':
        return '✕'; // X for error
      case 'success':
        return '✓'; // Check for success
      case 'warning':
        return '⚠'; // Warning triangle
      default:
        return '?'; // Question mark for unknown
    }
  };
  
  return (
    <span 
      className={getBadgeClasses()}
      role="status"
      aria-label={`Status: ${getDisplayText()}`}
      title={`Status: ${getDisplayText()}`}
    >
      <span className="status-icon me-1" aria-hidden="true">
        {getStatusIcon()}
      </span>
      {getDisplayText()}
    </span>
  );
};

export default StatusIndicator; 